/**
 * Event taxonomy — single source of truth for every custom event name.
 *
 * WHY constants (not free-form strings)?
 *  - Prevents typos ("signUp_completed" vs "signup_completed") that split funnels
 *  - Autocomplete + rename refactoring
 *  - Easy audit: search for `EVENTS.` to find every tracked event
 *
 * Naming convention: snake_case, "<domain>_<verb_past_tense>"
 *   - domain first so PostHog groups them nicely
 *   - past tense (event already happened when captured)
 *
 * Add new events here first, then call `track(EVENTS.X, {...props})`.
 */

export const EVENTS = Object.freeze({
  // --- Auth ---
  SIGNUP_STARTED: "signup_started",
  SIGNUP_COMPLETED: "signup_completed",
  LOGIN_SUCCESS: "login_success",
  LOGIN_FAILED: "login_failed",
  LOGOUT: "logout",
  EMAIL_VERIFIED: "email_verified",
  PASSWORD_RESET_REQUESTED: "password_reset_requested",
  PASSWORD_RESET_COMPLETED: "password_reset_completed",

  // --- Onboarding / profile ---
  PROFILE_CREATED: "profile_created",
  PROFILE_STEP_COMPLETED: "profile_step_completed",
  PROFILE_PUBLISHED: "profile_published",
  PROFILE_UPDATED: "profile_updated",
  CUSTOM_URL_CLAIMED: "custom_url_claimed",
  AVATAR_UPLOADED: "avatar_uploaded",

  // --- Discovery ---
  SEARCH_PERFORMED: "search_performed",
  SEARCH_RESULT_CLICKED: "search_result_clicked",
  PROFILE_VIEWED: "profile_viewed",
  FAVORITE_ADDED: "favorite_added",
  FAVORITE_REMOVED: "favorite_removed",
  DISCOVER_FILTER_APPLIED: "discover_filter_applied",

  // --- Booking funnel ---
  BOOKING_STARTED: "booking_started",
  BOOKING_SERVICE_SELECTED: "booking_service_selected",
  BOOKING_SLOT_SELECTED: "booking_slot_selected",
  BOOKING_FORM_SUBMITTED: "booking_form_submitted",
  BOOKING_PAID: "booking_paid",
  BOOKING_CONFIRMED: "booking_confirmed",
  BOOKING_ABANDONED: "booking_abandoned",

  // --- Seeker request flow ---
  REQUEST_CREATED: "request_created",
  REQUEST_LINK_OPENED: "request_link_opened",
  REQUEST_ACCEPTED: "request_accepted",
  REQUEST_DECLINED: "request_declined",
  REQUEST_EXPIRED: "request_expired",
  REQUEST_CANCELLED: "request_cancelled",
  REQUEST_COUNTERED: "request_countered",
  COUNTER_ACCEPTED: "counter_accepted",
  COUNTER_DECLINED: "counter_declined",
  MEETING_STATUS_CHANGED: "meeting_status_changed",

  // --- Payments ---
  PAYMENT_INTENT_CREATED: "payment_intent_created",
  PAYMENT_SUCCEEDED: "payment_succeeded",
  PAYMENT_FAILED: "payment_failed",
  REFUND_ISSUED: "refund_issued",
  PAYOUT_SENT: "payout_sent",
  PAYOUT_FAILED: "payout_failed",
  PAYOUT_METHOD_ADDED: "payout_method_added",
  PAYMENT_METHOD_ADDED: "payment_method_added",

  // --- Meeting lifecycle ---
  MEETING_SCHEDULED: "meeting_scheduled",
  MEETING_JOINED: "meeting_joined",
  MEETING_STARTED: "meeting_started",
  MEETING_ENDED: "meeting_ended",
  MEETING_NO_SHOW: "meeting_no_show",
  MEETING_CANCELLED: "meeting_cancelled",
  MEETING_RESCHEDULED: "meeting_rescheduled",
  MEETING_RATED: "meeting_rated",
  MEETING_REVIEWED: "meeting_reviewed",

  // --- Video room ---
  CAMERA_TOGGLED: "camera_toggled",
  MIC_TOGGLED: "mic_toggled",
  SCREEN_SHARE_STARTED: "screen_share_started",
  SCREEN_SHARE_STOPPED: "screen_share_stopped",
  VIRTUAL_BG_APPLIED: "virtual_bg_applied",
  CONNECTION_QUALITY_DEGRADED: "connection_quality_degraded",
  RECORDING_STARTED: "recording_started",
  RECORDING_STOPPED: "recording_stopped",

  // --- Engagement ---
  CHAT_MESSAGE_SENT: "chat_message_sent",
  NOTIFICATION_CLICKED: "notification_clicked",
  EMAIL_LINK_OPENED: "email_link_opened",
  PWA_INSTALLED: "pwa_installed",
  PUSH_SUBSCRIBED: "push_subscribed",
  PUSH_UNSUBSCRIBED: "push_unsubscribed",
  SHARE_ACTION: "share_action",

  // --- Support / AI ---
  SUPPORT_QUERY_SENT: "support_query_sent",
  FAQ_VIEWED: "faq_viewed",

  // --- Admin actions ---
  ADMIN_IMPERSONATE_STARTED: "admin_impersonate_started",
  ADMIN_IMPERSONATE_STOPPED: "admin_impersonate_stopped",
  ADMIN_MEETING_STATUS_CHANGED: "admin_meeting_status_changed",
  ADMIN_MEETING_DELETED: "admin_meeting_deleted",
  ADMIN_MEETING_EMAIL_RESENT: "admin_meeting_email_resent",
  ADMIN_NO_SHOW_CASE_CREATED: "admin_no_show_case_created",
  ADMIN_ROLE_CHANGED: "admin_role_changed",
  ADMIN_MEETING_ACTION: "admin_meeting_action",
  ADMIN_MODERATION_RESOLVED: "admin_moderation_resolved",
  ADMIN_REFUND_ISSUED: "admin_refund_issued",

  // --- Errors ---
  CLIENT_ERROR: "client_error",
  API_ERROR: "api_error",
});

/**
 * Common property helpers — keep property shapes consistent across events.
 */
export function bookingProps({
  bookingId,
  providerId,
  seekerId,
  serviceId,
  amount,
  currency,
  step,
} = {}) {
  return {
    booking_id: bookingId,
    provider_id: providerId,
    seeker_id: seekerId,
    service_id: serviceId,
    amount,
    currency,
    step,
  };
}

export function meetingProps({
  meetingId,
  providerId,
  seekerId,
  durationSeconds,
  role,
  reason,
} = {}) {
  return {
    meeting_id: meetingId,
    provider_id: providerId,
    seeker_id: seekerId,
    duration_seconds: durationSeconds,
    role,
    reason,
  };
}

export function paymentProps({
  amount,
  currency,
  provider, // stripe | paypal
  bookingId,
  intentId,
  reason,
} = {}) {
  return {
    amount,
    currency,
    provider,
    booking_id: bookingId,
    intent_id: intentId,
    reason,
  };
}
